import React from 'react';
import { User } from 'lucide-react';

interface UserAvatarProps {
    /** URL of user's avatar image */
    avatarUrl?: string | null;
    /** User's full name for alt text and fallback */
    fullName?: string;
    /** Size variant */
    size?: 'sm' | 'md' | 'lg';
}

/** Size configurations */
const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-9 h-9',
    lg: 'w-12 h-12',
} as const;

const iconSizes = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
} as const;

/**
 * UserAvatar Component
 * 
 * Displays user avatar image or fallback icon.
 * Handles loading states and missing images gracefully.
 * 
 * @example
 * <UserAvatar avatarUrl={user.avatarUrl} fullName={user.fullName} />
 */
const UserAvatar: React.FC<UserAvatarProps> = ({
    avatarUrl,
    fullName = 'User',
    size = 'md',
}) => {
    const sizeClass = sizeClasses[size];
    const iconSize = iconSizes[size];

    // Has valid avatar URL
    if (avatarUrl) {
        return (
            <img
                src={avatarUrl}
                alt={`${fullName}'s avatar`}
                className={`${sizeClass} rounded-full object-cover border-2 border-haat-red/20`}
                loading="lazy"
            />
        );
    }

    // Fallback - show icon
    return (
        <div
            className={`${sizeClass} rounded-full bg-haat-red/10 flex items-center justify-center`}
            role="img"
            aria-label={`${fullName}'s avatar`}
        >
            <User className={`${iconSize} text-haat-red`} />
        </div>
    );
};

export default UserAvatar;

